import React, { useState, useEffect } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { Tabs, Tab, Box, AppBar } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import AssessmentIcon from '@mui/icons-material/Assessment';
import Login from './components/Login';
import DailyReport from './components/DailyReport';
import AttendanceReport from './components/AttendanceReport';
import MachinePartsReport from './components/MachinePartsReport';
import DailySummaryReport from './components/DailySummaryReport';
import Settings from './components/Settings';
import './App.css';

const theme = createTheme({
  palette: {
    primary: {
      main: '#667eea',
    },
    secondary: {
      main: '#764ba2',
    },
    background: {
      default: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
    },
  },
  typography: {
    fontFamily: "'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
});

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentTab, setCurrentTab] = useState(0);
  const [userRole, setUserRole] = useState(null);

  // Check if user is already logged in on component mount
  useEffect(() => {
    const authStatus = localStorage.getItem('isAuthenticated');
    const role = localStorage.getItem('userRole');
    if (authStatus === 'true') {
      setIsAuthenticated(true);
      setUserRole(role);
      // If HR admin, set default tab to 0 (Attendance will be the only tab at index 0)
      if (role === 'hradmin') {
        setCurrentTab(0);
      }
    }
    setIsLoading(false);
  }, []);

  const handleLogin = () => {
    const role = localStorage.getItem('userRole');
    setIsAuthenticated(true);
    setUserRole(role);
    // If HR admin, set default tab to 0 (Attendance will be the only tab at index 0)
    if (role === 'hradmin') {
      setCurrentTab(0);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('username');
    localStorage.removeItem('userRole');
    setIsAuthenticated(false);
    setUserRole(null);
    setCurrentTab(0);
    console.log('✅ Logged out successfully');
  };

  // Determine which tabs to show based on user role
  const isAdmin = userRole === 'admin';
  const isHRAdmin = userRole === 'hradmin';

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="App">
        <div className="loading-screen">
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <div className="App">
        {isAuthenticated ? (
          <Box sx={{ width: '100%' }}>
            <AppBar position="static" color="default" elevation={1}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2 }}>
                <Tabs 
                  value={currentTab} 
                  onChange={(e, newValue) => {
                    setCurrentTab(newValue);
                  }} 
                  sx={{ flexGrow: 1 }}
                >
                  {isAdmin && (
                    <Tab icon={<AssessmentIcon />} label="Daily Summary Report" iconPosition="start" />
                  )}
                  {isAdmin && (
                    <Tab icon={<DashboardIcon />} label="Daily Production" iconPosition="start" />
                  )}
                  <Tab icon={<AssignmentIndIcon />} label="Attendance Report" iconPosition="start" />
                  <Tab icon={<AssessmentIcon />} label="Machine Parts Report" iconPosition="start" />
                  {isAdmin && (
                    <Tab icon={<SettingsIcon />} label="Settings" iconPosition="start" />
                  )}
                </Tabs>
                <Tab 
                  icon={<LogoutIcon />} 
                  label="Logout" 
                  iconPosition="start" 
                  onClick={handleLogout}
                  sx={{ minHeight: '48px' }}
                />
              </Box>
            </AppBar>
            
            {/* Render content based on user role and current tab */}
            {isAdmin && currentTab === 0 && <DailySummaryReport />}
            {isAdmin && currentTab === 1 && <DailyReport onLogout={handleLogout} />}
            {(isAdmin && currentTab === 2) || (isHRAdmin && currentTab === 0) ? <AttendanceReport /> : null}
            {(isAdmin && currentTab === 3) || (isHRAdmin && currentTab === 1) ? <MachinePartsReport /> : null}
            {isAdmin && currentTab === 4 && <Settings />}
          </Box>
        ) : (
          <Login onLogin={handleLogin} />
        )}
      </div>
    </ThemeProvider>
  );
}

export default App;

